import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, RefreshCw, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

type SnapshotRow = {
  nh_slug: string;
  center_name: string | null;
  county: string | null;
  county_slug: string | null;
  claimed_at: string | null;
  owner_user_id: string;
  owner_email: string | null;
  subscription_status: string;
  plan_name: string | null;
  current_period_end: string | null;
  lead_management: boolean;
  direct_leads: number;
  waitlist_leads: number;
};

function num(v: string | number | null | undefined): number {
  if (v == null) return 0;
  return typeof v === "number" ? v : Number(v);
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function ClaimedHomesRevenueSnapshotPage() {
  const [rows, setRows] = useState<SnapshotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("admin_claimed_homes_revenue_snapshot");
      if (error) throw error;
      const mapped = (data ?? []).map((r) => ({
        nh_slug: r.nh_slug,
        center_name: r.center_name,
        county: r.county,
        county_slug: r.county_slug,
        claimed_at: r.claimed_at,
        owner_user_id: r.owner_user_id,
        owner_email: r.owner_email,
        subscription_status: r.subscription_status,
        plan_name: r.plan_name,
        current_period_end: r.current_period_end,
        lead_management: Boolean(r.lead_management),
        direct_leads: num(r.direct_leads as string | number),
        waitlist_leads: num(r.waitlist_leads as string | number),
      }));
      setRows(mapped);
    } catch (e) {
      console.error(e);
      toast.error("Could not load snapshot");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.center_name ?? "").toLowerCase().includes(q) ||
        r.nh_slug.toLowerCase().includes(q) ||
        (r.county ?? "").toLowerCase().includes(q) ||
        (r.owner_email ?? "").toLowerCase().includes(q) ||
        (r.plan_name ?? "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const paying = rows.filter((r) =>
    ["active", "trialing"].includes(r.subscription_status),
  ).length;
  const withLeadSku = rows.filter((r) => r.lead_management).length;

  return (
    <AdminLayout>
      <Helmet>
        <title>Claimed homes & revenue snapshot | Grandpal Admin</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Claimed homes snapshot</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Subscription status per owner, lead-management flag, and lead signal counts (direct +
              county waitlist).
            </p>
          </div>
          <Button variant="outline" onClick={() => load()} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Claimed homes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{rows.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Paying subscription
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{paying}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Lead management on plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{withLeadSku}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Homes</CardTitle>
            <CardDescription>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Input
                  placeholder="Filter by name, slug, county, email, plan…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="max-w-md"
                />
                <span className="text-sm self-center text-muted-foreground">
                  Showing {filtered.length} of {rows.length}
                </span>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-12 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Home</TableHead>
                    <TableHead>County</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Renews / ends</TableHead>
                    <TableHead>Leads SKU</TableHead>
                    <TableHead className="text-right">Direct</TableHead>
                    <TableHead className="text-right">Waitlist</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const total = r.direct_leads + r.waitlist_leads;
                    const publicHref =
                      r.county_slug && r.nh_slug
                        ? `/nursing-homes/${r.county_slug}/${r.nh_slug}`
                        : `/nursing-homes/lookup/${r.nh_slug}`;
                    return (
                      <TableRow key={r.nh_slug}>
                        <TableCell>
                          <div className="font-medium">{r.center_name ?? r.nh_slug}</div>
                          <div className="text-xs text-muted-foreground font-mono">{r.nh_slug}</div>
                          <Link
                            to={publicHref}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-primary inline-flex items-center gap-1 mt-1"
                          >
                            Public profile <ExternalLink className="h-3 w-3" />
                          </Link>
                        </TableCell>
                        <TableCell>{r.county ?? "—"}</TableCell>
                        <TableCell>
                          {r.owner_email ? (
                            <a href={`mailto:${r.owner_email}`} className="text-primary text-sm">
                              {r.owner_email}
                            </a>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                          <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                            {r.owner_user_id.slice(0, 8)}…
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant={
                                r.subscription_status === "none" ? "secondary" : "default"
                              }
                            >
                              {r.subscription_status}
                            </Badge>
                            {r.plan_name ? (
                              <span className="text-xs text-muted-foreground">{r.plan_name}</span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {formatShortDate(r.current_period_end)}
                        </TableCell>
                        <TableCell>
                          {r.lead_management ? (
                            <Badge variant="outline" className="border-green-600 text-green-800">
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.direct_leads}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.waitlist_leads}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">{total}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
